/**
 * Service für Geocoding und Distanzberechnungen
 */
export const locationService = {
  /**
   * Wandelt eine Adresse in Koordinaten um.
   * Nutzt primär Photon (Komoot) für extrem hohe Fehlertoleranz und Nominatim als Fallback.
   */
  async geocode(address) {
    if (!address) return null;
    
    // 1. Versuch: Photon (Komoot) - sehr fehlertolerant, gut für PLZ/Stadt Kombinationen
    try {
      const photonRes = await fetch(
        `https://photon.komoot.io/api/?q=${encodeURIComponent(address)}&limit=1`
      );
      if (photonRes.ok) {
        const data = await photonRes.json();
        if (data && data.features && data.features.length > 0) {
          const coords = data.features[0].geometry.coordinates; // Photon liefert [lon, lat]
          const props = data.features[0].properties;
          return {
            lat: parseFloat(coords[1]),
            lng: parseFloat(coords[0]),
            display_name: [props.name, props.city, props.postcode].filter(Boolean).join(', ') || address
          };
        }
      }
    } catch (error) {
      console.warn("Photon Geocoding fehlgeschlagen, versuche Fallback...", error);
    }

    // 2. Versuch: Nominatim OpenStreetMap (Fallback)
    try {
      const nomRes = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address + ', Deutschland')}&limit=1`
      );
      if (nomRes.ok) {
        const data = await nomRes.json();
        if (data && data.length > 0) {
          return {
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lon),
            display_name: data[0].display_name
          };
        }
      }
    } catch (error) {
      console.error("Nominatim Geocoding Fehler:", error);
    }

    return null;
  },

  /**
   * Wandelt Koordinaten in eine Adresse um (Reverse Geocoding).
   * Wird für die automatische Standortermittlung des Browsers verwendet.
   */
  async reverseGeocode(lat, lng) {
    if (!lat || !lng) return null;
    
    try {
      const photonRes = await fetch(
        `https://photon.komoot.io/reverse?lon=${lng}&lat=${lat}`
      );
      if (photonRes.ok) {
        const data = await photonRes.json();
        if (data && data.features && data.features.length > 0) {
          const props = data.features[0].properties;
          return {
            lat,
            lng,
            display_name: [props.name, props.city || props.town || props.village, props.postcode].filter(Boolean).join(', ') || null
          };
        }
      }
    } catch (error) {
      console.warn("Photon Reverse Geocoding fehlgeschlagen, versuche Fallback...", error);
    }

    try {
      const nomRes = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      if (nomRes.ok) {
        const data = await nomRes.json();
        if (data && data.display_name) {
          const addr = data.address;
          const shortName = addr ? [addr.city || addr.town || addr.village, addr.postcode].filter(Boolean).join(', ') : null;
          return {
            lat,
            lng,
            display_name: shortName || data.display_name
          };
        }
      }
    } catch (error) {
      console.error("Nominatim Reverse Geocoding Fehler:", error);
    }

    return { lat, lng, display_name: null };
  },

  /**
   * Berechnet die Entfernung zwischen zwei Punkten in Kilometern (Haversine Formel)
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const l1 = parseFloat(lat1);
    const lg1 = parseFloat(lon1);
    const l2 = parseFloat(lat2);
    const lg2 = parseFloat(lon2);

    // Falls Koordinaten ungültig/leer sind
    if (isNaN(l1) || isNaN(lg1) || isNaN(l2) || isNaN(lg2)) return 999;

    const R = 6371; // Erdradius in km
    const dLat = (l2 - l1) * Math.PI / 180;
    const dLon = (lg2 - lg1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(l1 * Math.PI / 180) * Math.cos(l2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }
};
